import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCases } from '@/hooks/use-cases';
import { getLocalizedText } from '@/lib/utils';
import { Plus, Star } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const statusLabels = {
  draft: 'Concept',
  in_review: 'Ter review',
  changes_requested: 'Aanpassingen gevraagd',
  published: 'Gepubliceerd',
} as const;
const categoryLabels = { incentive: 'Incentive', event: 'Event', luxury: 'Luxury Travel' } as const;
type CategoryFilter = 'all' | keyof typeof categoryLabels;
const dateFormat = new Intl.DateTimeFormat('nl-BE', { dateStyle: 'medium' });

export function CasesPage() {
  const { data: cases, isLoading } = useCases();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const visibleCases = (cases ?? []).filter(item => category === 'all' || item.category === category);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cases</h1>
          <p className="mt-1 text-muted-foreground">Beheer inspirerende reizen, events en bestemmingen.</p>
        </div>
        <Button asChild>
          <Link to="/cases/nieuw">
            <Plus className="mr-2 h-4 w-4" />
            Nieuwe case
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Meest recent aangemaakte case eerst.</p>
        <label className="flex items-center gap-2 text-sm font-medium">
          Toon
          <select
            value={category}
            onChange={event => setCategory(event.target.value as CategoryFilter)}
            className="h-9 rounded-md border bg-background px-2 font-normal"
          >
            <option value="all">Alle types</option>
            <option value="event">Events</option>
            <option value="incentive">Incentives</option>
            <option value="luxury">Luxury Travel</option>
          </select>
        </label>
      </div>
      {isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <div className="rounded-md border">
          {visibleCases.map(item => (
            <Link
              key={item.id}
              to={`/cases/${item.id}/bewerken`}
              className="flex flex-wrap items-center justify-between gap-3 border-b p-4 last:border-0 hover:bg-muted/50"
            >
              <div>
                <p className="font-medium">{getLocalizedText(item.title)}</p>
                <p className="text-sm text-muted-foreground">
                  {categoryLabels[item.category]} · /{item.slug} · Aangemaakt{' '}
                  {dateFormat.format(new Date(item.createdAt))}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {item.isFeatured && (
                  <Badge variant="secondary">
                    <Star className="mr-1 size-3 fill-current" />
                    Uitgelicht · prio {item.sortOrder}
                  </Badge>
                )}
                <Badge variant={item.status === 'published' ? 'success' : 'secondary'}>
                  {statusLabels[item.status]}
                </Badge>
              </div>
            </Link>
          ))}
          {!visibleCases.length && (
            <p className="p-8 text-center text-muted-foreground">
              {category === 'all' ? 'Nog geen cases aangemaakt.' : 'Geen cases voor dit type.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
